import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CLIENT_LIST_COLUMNS } from "./table-config";
import { ButtonWithIcon } from "../../../components/buttons";
import {
  Box,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import clientHOC from "../client-hoc";
import Searchbox from "../../../components/searchbox";
import { ClientListTable } from "./tables";
import { downloadClientListCsv } from "./download-utils";
import ViewersList from "../../opportunity/response-list/viewers-list-modal";
import { getClientsForDownload, restoreClient } from "../../../apis/clients.api";
import { AppAlertBox } from "../../../components/modals/alert-box";
import { showNotification } from "../../../services/utils";

function ClientsList({
  onAddClientClick,
  toggleMenuOptions,
  getClientList,
  clientList,
}: any) {
  const dispatch = useDispatch();
  const { selectedPage, selectedPageSize, searchValue, isDeleted } = useSelector(
    (state: any) => state.clients.list.filters
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [opportunityVendorId, setOpportunityVendorId] = useState(null);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const listParams = {
    selectedPage,
    selectedPageSize,
    searchValue,
    isDeleted,
  };

  const handleShowDetailsClick = (row) => {
    setShowDetailsModal(true);
    setOpportunityVendorId(row?.id);
  };

  useEffect(() => {
    getClientList(listParams);
  }, []);

  const handlePageFilters = ({ selectedPage, selectedPageSize }) => {
    getClientList({
      selectedPage,
      selectedPageSize,
      searchValue,
      isDeleted,
    });
  };

  const handleArchivedOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    getClientList({
      selectedPage: 1,
      selectedPageSize,
      searchValue,
      isDeleted: event.target.checked,
    });
  };

  const handleUnarchiveClick = (id: string) => {
    setSelectedClientId(id);
    setShowUnarchiveConfirm(true);
  };

  const handleUnarchiveConfirm = async () => {
    if (!selectedClientId) return;

    try {
      const res: any = await dispatch(restoreClient(selectedClientId));
      if (res.success) {
        showNotification({
          message: "Client unarchived successfully",
          type: "success",
        });
        setShowUnarchiveConfirm(false);
        setSelectedClientId(null);
        getClientList(listParams);
      } else {
        showNotification({
          message: res.message || "Failed to unarchive client",
          type: "error",
        });
      }
    } catch (err) {
      showNotification({
        message: "Failed to unarchive client",
        type: "error",
      });
    }
  };

  const handleUnarchiveCancel = () => {
    setShowUnarchiveConfirm(false);
    setSelectedClientId(null);
  };

  const handleModalClose = () => {
    setShowDetailsModal(false);
    setOpportunityVendorId(null);
  };

  const handleDownload = async () => {
    const total = clientList?.total;
    if (!total) return;

    setIsDownloading(true);

    try {
      const res: any = await getClientsForDownload({
        total,
        searchValue,
        isDeleted,
      });

      if (res?.success && res?.data?.length) {
        downloadClientListCsv(res.data);
      } else {
        showNotification({
          message: res?.message || "Failed to download client list",
          type: "error",
        });
      }
    } catch {
      showNotification({
        message: "Failed to download client list",
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Grid container alignItems={"center"} mb={2} spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Searchbox
            onEnter={(value) =>
              getClientList({
                searchValue: value,
                selectedPage: 1,
                selectedPageSize,
                isDeleted,
              })
            }
            value={searchValue}
            onClear={() => {
              getClientList({
                searchValue: "",
                selectedPage: 1,
                selectedPageSize,
                isDeleted,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="flex-end"
            flexWrap="wrap"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(isDeleted)}
                  onChange={handleArchivedOnlyChange}
                  color="primary"
                />
              }
              label="Archived Users"
            />
            <ButtonWithIcon
              handleOnClick={onAddClientClick}
              text={"ADD"}
              icon={{ position: "startIcon", name: "add" }}
            />
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2} direction="column">
        <Grid item>
          {Boolean(clientList?.total) && (
            <Stack direction="row" justifyContent="flex-end" mb={1}>
              <ButtonWithIcon
                handleOnClick={isDownloading ? undefined : handleDownload}
                text={isDownloading ? "DOWNLOADING..." : "DOWNLOAD"}
                icon={{ position: "startIcon", name: "download" }}
                muiIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  opacity: isDownloading ? 0.7 : 1,
                  pointerEvents: isDownloading ? "none" : "auto",
                }}
              />
            </Stack>
          )}
          <ClientListTable
            list={clientList}
            toggleMenuOptions={toggleMenuOptions}
            columns={CLIENT_LIST_COLUMNS}
            onShowViewCountDetailsClick={handleShowDetailsClick}
            handlePageFilters={handlePageFilters}
            appliedFilters={{
              selectedPageSize,
              searchValue,
              selectedPage,
            }}
            isDeleted={Boolean(isDeleted)}
            onUnarchiveClick={handleUnarchiveClick}
          />
        </Grid>
      </Grid>
      {showDetailsModal && (
        <ViewersList
          opportunityVendorId={opportunityVendorId}
          handleModalClose={handleModalClose}
        />
      )}

      <AppAlertBox
        isModalOpen={showUnarchiveConfirm}
        handleClose={handleUnarchiveCancel}
        onConfirm={handleUnarchiveConfirm}
        onCancel={handleUnarchiveCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Are you sure you want to unarchive this client?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              <strong>Unarchive:</strong> This will restore the client and they will no longer appear
              in archived clients.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />
    </>
  );
}

export default clientHOC(React.memo(ClientsList));
